export interface TimelineCoordinate {
  readonly latitude: number;
  readonly longitude: number;
}

export interface TimelinePoint extends TimelineCoordinate {
  readonly timestamp: Date;
}

export interface TimelineVisit {
  readonly coordinate: TimelineCoordinate;
  readonly startTime: Date;
  readonly endTime: Date;
  readonly semanticType: 'HOME' | 'WORK' | 'UNKNOWN';
}

export interface TimelineActivity {
  readonly start: TimelineCoordinate | null;
  readonly end: TimelineCoordinate | null;
  readonly startTime: Date;
  readonly endTime: Date;
  readonly type: string;
  readonly distanceKm: number;
}

export interface TimelinePlace {
  readonly coordinate: TimelineCoordinate;
  readonly kind: 'home' | 'work';
  readonly label: string;
  readonly confidence: 'explicit' | 'estimated';
}

export interface TimelineTrip {
  readonly startTime: Date;
  readonly endTime: Date;
  readonly coordinate: TimelineCoordinate;
  readonly distanceKm: number;
  readonly countryCode: string | null;
}

export interface TimelineCountry {
  readonly code: string;
  readonly name: string;
  readonly flag: string;
}

export interface TimelineAnalysis {
  readonly points: TimelinePoint[];
  readonly visits: TimelineVisit[];
  readonly activities: TimelineActivity[];
  readonly home: TimelinePlace | null;
  readonly work: TimelinePlace | null;
  readonly commuteDays: number;
  readonly trips: TimelineTrip[];
  readonly countries: TimelineCountry[];
  readonly totalDistanceKm: number;
  readonly periodDayCount: number;
  readonly recordedDayCount: number;
}

export interface TimelineMonth {
  readonly value: string;
  readonly label: string;
}

type JsonObject = Record<string, unknown>;

interface SemanticRecord {
  readonly value: JsonObject;
  readonly priority: number;
}

interface ClusterSample {
  readonly coordinate: TimelineCoordinate;
  readonly dateKey: string;
  readonly weight: number;
}

interface CoordinateCluster {
  latitude: number;
  longitude: number;
  weight: number;
  readonly dates: Set<string>;
  sampleCount: number;
}

export class TimelineError extends Error {
  constructor(
    public readonly code: 'malformed' | 'unsupported' | 'no-route',
    message: string,
  ) {
    super(message);
    this.name = 'TimelineError';
  }
}

function isObject(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function normalizeDegrees(value: number): number {
  return Math.abs(value) > 1_000_000 ? value / 10_000_000 : value;
}

export function parseCoordinate(value: unknown): [number, number] | null {
  if (isObject(value)) {
    const nested = value.latLng ?? value.LatLng ?? value.point ?? value.location;
    if (nested !== undefined && nested !== value) {
      const parsed = parseCoordinate(nested);
      if (parsed) return parsed;
    }

    const latitude = finiteNumber(
      value.latitude ?? value.lat ?? value.latitudeE7 ?? value.latE7,
    );
    const longitude = finiteNumber(
      value.longitude ?? value.lng ?? value.lon ?? value.longitudeE7 ?? value.lngE7 ?? value.lonE7,
    );
    if (latitude !== null && longitude !== null) {
      return validateCoordinate(normalizeDegrees(latitude), normalizeDegrees(longitude));
    }
    return null;
  }

  if (Array.isArray(value) && value.length >= 2) {
    const latitude = finiteNumber(value[0]);
    const longitude = finiteNumber(value[1]);
    if (latitude !== null && longitude !== null) {
      return validateCoordinate(normalizeDegrees(latitude), normalizeDegrees(longitude));
    }
  }

  if (typeof value !== 'string' || value.trim() === '') return null;
  const cleaned = value
    .trim()
    .replace(/^geo:/i, '')
    .split('?', 1)[0]
    .replaceAll('°', '')
    .replaceAll(' ', '');
  const [rawLatitude, rawLongitude] = cleaned.split(',');
  const latitude = finiteNumber(rawLatitude);
  const longitude = finiteNumber(rawLongitude);
  if (latitude === null || longitude === null) return null;
  return validateCoordinate(normalizeDegrees(latitude), normalizeDegrees(longitude));
}

function validateCoordinate(latitude: number, longitude: number): [number, number] | null {
  if (latitude < -85.05112878 || latitude > 85.05112878) return null;
  if (longitude < -180 || longitude > 180) return null;
  return [latitude, longitude];
}

function parseTimestamp(value: unknown): Date | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toCoordinate(value: unknown): TimelineCoordinate | null {
  const coordinate = parseCoordinate(value);
  return coordinate ? { latitude: coordinate[0], longitude: coordinate[1] } : null;
}

function addPoint(
  points: TimelinePoint[],
  coordinateValue: unknown,
  timestampValue: unknown,
): void {
  const coordinate = parseCoordinate(coordinateValue);
  const timestamp = parseTimestamp(timestampValue);
  if (!coordinate || !timestamp) return;
  points.push({ latitude: coordinate[0], longitude: coordinate[1], timestamp });
}

function segmentPayload(record: JsonObject): JsonObject {
  return isObject(record.segment) ? record.segment : record;
}

function addSemanticSegmentPoints(points: TimelinePoint[], record: JsonObject): void {
  const startTime = record.startTime ?? record.startTimestamp;
  const endTime = record.endTime ?? record.endTimestamp;
  const segment = segmentPayload(record);
  const activity = isObject(segment.activity) ? segment.activity : null;
  if (activity) {
    addPoint(points, activity.start ?? activity.startLocation, startTime);
    addPoint(points, activity.end ?? activity.endLocation, endTime);
  }

  const visit = isObject(segment.visit) ? segment.visit : null;
  const candidate = visit && isObject(visit.topCandidate) ? visit.topCandidate : null;
  if (candidate) addPoint(points, candidate.placeLocation ?? candidate.location, startTime);

  const path = Array.isArray(segment.timelinePath) ? segment.timelinePath : [];
  for (const rawPathPoint of path) {
    if (!isObject(rawPathPoint)) continue;
    let timestamp: unknown = rawPathPoint.time ?? rawPathPoint.timestamp;
    if (timestamp === undefined) {
      const offset = finiteNumber(rawPathPoint.durationMinutesOffsetFromStartTime);
      const start = parseTimestamp(startTime);
      if (offset !== null && start) timestamp = start.getTime() + offset * 60_000;
    }
    addPoint(points, rawPathPoint.point ?? rawPathPoint.location, timestamp);
  }
}

function collectSemanticRecords(data: unknown): SemanticRecord[] {
  const records: SemanticRecord[] = [];
  if (isObject(data) && Array.isArray(data.semanticSegments)) {
    for (const value of data.semanticSegments) {
      if (isObject(value)) records.push({ value, priority: 1 });
    }
  } else if (Array.isArray(data)) {
    for (const value of data) {
      if (isObject(value)) records.push({ value, priority: 1 });
    }
  }

  if (isObject(data) && Array.isArray(data.timelineEdits)) {
    for (const rawEdit of data.timelineEdits) {
      if (!isObject(rawEdit)) continue;
      if (isObject(rawEdit.inferredSemanticSegment)) {
        records.push({ value: rawEdit.inferredSemanticSegment, priority: 1 });
      }
      if (isObject(rawEdit.userEditedSemanticSegment)) {
        records.push({ value: rawEdit.userEditedSemanticSegment, priority: 2 });
      }
    }
  }
  return records;
}

function semanticRecordKey(record: JsonObject): string | null {
  const startTime = parseTimestamp(record.startTime ?? record.startTimestamp);
  const endTime = parseTimestamp(record.endTime ?? record.endTimestamp);
  if (!startTime || !endTime) return null;
  const segment = segmentPayload(record);
  const kind = isObject(segment.visit) ? 'visit' : isObject(segment.activity) ? 'activity' : 'path';
  return `${startTime.toISOString()}:${endTime.toISOString()}:${kind}`;
}

function preferredSemanticRecords(data: unknown): JsonObject[] {
  const preferred = new Map<string, SemanticRecord>();
  for (const record of collectSemanticRecords(data)) {
    const key = semanticRecordKey(record.value);
    if (!key) continue;
    const previous = preferred.get(key);
    if (!previous || previous.priority <= record.priority) preferred.set(key, record);
  }
  return [...preferred.values()].map((record) => record.value);
}

function semanticType(value: unknown): 'HOME' | 'WORK' | 'UNKNOWN' {
  if (typeof value !== 'string') return 'UNKNOWN';
  const normalized = value.toUpperCase();
  if (normalized === 'HOME' || normalized === 'WORK') return normalized;
  return 'UNKNOWN';
}

function parseVisits(records: readonly JsonObject[]): TimelineVisit[] {
  const visits: TimelineVisit[] = [];
  for (const record of records) {
    const startTime = parseTimestamp(record.startTime ?? record.startTimestamp);
    const endTime = parseTimestamp(record.endTime ?? record.endTimestamp);
    const visit = segmentPayload(record).visit;
    if (!startTime || !endTime || !isObject(visit)) continue;
    const candidate = isObject(visit.topCandidate) ? visit.topCandidate : null;
    const coordinate = candidate
      ? toCoordinate(candidate.placeLocation ?? candidate.location)
      : null;
    if (!coordinate) continue;
    visits.push({
      coordinate,
      startTime,
      endTime,
      semanticType: semanticType(candidate?.semanticType),
    });
  }
  return visits.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
}

function parseActivities(records: readonly JsonObject[]): TimelineActivity[] {
  const activities: TimelineActivity[] = [];
  for (const record of records) {
    const startTime = parseTimestamp(record.startTime ?? record.startTimestamp);
    const endTime = parseTimestamp(record.endTime ?? record.endTimestamp);
    const activity = segmentPayload(record).activity;
    if (!startTime || !endTime || !isObject(activity)) continue;
    const start = toCoordinate(activity.start ?? activity.startLocation);
    const end = toCoordinate(activity.end ?? activity.endLocation);
    const topCandidate = isObject(activity.topCandidate) ? activity.topCandidate : null;
    const distanceMeters = finiteNumber(activity.distanceMeters);
    const directDistance = start && end ? coordinateDistanceKm(start, end) : 0;
    activities.push({
      start,
      end,
      startTime,
      endTime,
      type: typeof topCandidate?.type === 'string' ? topCandidate.type : 'UNKNOWN',
      distanceKm: distanceMeters === null ? directDistance : Math.max(0, distanceMeters / 1_000),
    });
  }
  return activities.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
}

function collectStateHomeHints(data: unknown): TimelineCoordinate[] {
  if (!isObject(data) || !Array.isArray(data.timelineEdits)) return [];
  const hints: TimelineCoordinate[] = [];
  for (const rawEdit of data.timelineEdits) {
    if (!isObject(rawEdit) || !isObject(rawEdit.semanticLocationStates)) continue;
    const events = Array.isArray(rawEdit.semanticLocationStates.events)
      ? rawEdit.semanticLocationStates.events
      : [];
    for (const rawEvent of events) {
      if (!isObject(rawEvent)) continue;
      for (const rawPlace of [rawEvent.enterPlace, rawEvent.exitPlace]) {
        if (!isObject(rawPlace) || !isObject(rawPlace.topCandidate)) continue;
        if (semanticType(rawPlace.topCandidate.semanticType) !== 'HOME') continue;
        const coordinate = toCoordinate(
          rawPlace.topCandidate.placeLocation ?? rawPlace.topCandidate.location,
        );
        if (coordinate) hints.push(coordinate);
      }
    }
  }
  return hints;
}

function extractPoints(data: unknown, semanticRecords: readonly JsonObject[]): TimelinePoint[] {
  const points: TimelinePoint[] = [];
  for (const record of semanticRecords) addSemanticSegmentPoints(points, record);

  // Current phone exports wrap raw positions in timelineEdits/rawSignal. A
  // bounded walk accepts those wrappers while the semantic parser above keeps
  // visits and activities instead of flattening everything into coordinates.
  const queue: unknown[] = [data];
  let visited = 0;
  const maximumNodes = 2_000_000;
  while (queue.length > 0 && visited < maximumNodes) {
    const value = queue.pop();
    visited += 1;
    if (Array.isArray(value)) {
      for (const child of value) queue.push(child);
      continue;
    }
    if (!isObject(value)) continue;

    const directTimestamp = value.timestamp ?? value.time ?? value.instant;
    const directCoordinate = value.point
      ?? value.latLng
      ?? value.LatLng
      ?? value.coordinate
      ?? (('latitude' in value || 'latitudeE7' in value) ? value : undefined);
    if (directTimestamp !== undefined && directCoordinate !== undefined) {
      addPoint(points, directCoordinate, directTimestamp);
    }

    const position = isObject(value.position) ? value.position : null;
    if (position) {
      addPoint(
        points,
        position.point ?? position.latLng ?? position.LatLng ?? position,
        position.timestamp ?? position.time ?? directTimestamp,
      );
    }

    for (const child of Object.values(value)) {
      if (typeof child === 'object' && child !== null) queue.push(child);
    }
  }

  const unique = new Map<string, TimelinePoint>();
  for (const point of points) {
    const key = `${point.timestamp.getTime()}:${point.latitude.toFixed(7)}:${point.longitude.toFixed(7)}`;
    unique.set(key, point);
  }
  const sorted = [...unique.values()].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const distinct: TimelinePoint[] = [];
  for (const point of sorted) {
    const previous = distinct.at(-1);
    if (!previous || previous.latitude !== point.latitude || previous.longitude !== point.longitude) {
      distinct.push(point);
    }
  }
  if (distinct.length < 2) {
    throw new TimelineError('no-route', '이 파일에서는 이동 기록을 찾지 못했어요.');
  }
  return thinTimeline(distinct, 6_000);
}

export function coordinateDistanceKm(a: TimelineCoordinate, b: TimelineCoordinate): number {
  const radians = (value: number): number => value * Math.PI / 180;
  const latitude = radians(b.latitude - a.latitude);
  const longitude = radians(b.longitude - a.longitude);
  const halfChord = Math.sin(latitude / 2) ** 2
    + Math.cos(radians(a.latitude)) * Math.cos(radians(b.latitude))
    * Math.sin(longitude / 2) ** 2;
  return 12_742 * Math.asin(Math.min(1, Math.sqrt(halfChord)));
}

function localDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function averageCoordinate(coordinates: readonly TimelineCoordinate[]): TimelineCoordinate | null {
  if (coordinates.length === 0) return null;
  return {
    latitude: coordinates.reduce((sum, value) => sum + value.latitude, 0) / coordinates.length,
    longitude: coordinates.reduce((sum, value) => sum + value.longitude, 0) / coordinates.length,
  };
}

function clusterSamples(samples: readonly ClusterSample[], radiusKm: number): CoordinateCluster[] {
  const clusters: CoordinateCluster[] = [];
  for (const sample of samples) {
    let target: CoordinateCluster | null = null;
    let closest = Number.POSITIVE_INFINITY;
    for (const cluster of clusters) {
      const distance = coordinateDistanceKm(cluster, sample.coordinate);
      if (distance <= radiusKm && distance < closest) {
        target = cluster;
        closest = distance;
      }
    }
    if (!target) {
      target = {
        latitude: sample.coordinate.latitude,
        longitude: sample.coordinate.longitude,
        weight: 0,
        dates: new Set<string>(),
        sampleCount: 0,
      };
      clusters.push(target);
    }
    const nextWeight = target.weight + sample.weight;
    target.latitude = (target.latitude * target.weight + sample.coordinate.latitude * sample.weight) / nextWeight;
    target.longitude = (target.longitude * target.weight + sample.coordinate.longitude * sample.weight) / nextWeight;
    target.weight = nextWeight;
    target.sampleCount += 1;
    target.dates.add(sample.dateKey);
  }
  return clusters;
}

function deriveHome(
  points: readonly TimelinePoint[],
  visits: readonly TimelineVisit[],
  stateHints: readonly TimelineCoordinate[],
): TimelinePlace | null {
  const explicit = [
    ...visits.filter((visit) => visit.semanticType === 'HOME').map((visit) => visit.coordinate),
    ...stateHints,
  ];
  const explicitCoordinate = averageCoordinate(explicit);
  if (explicitCoordinate) {
    return { coordinate: explicitCoordinate, kind: 'home', label: '집', confidence: 'explicit' };
  }

  const nightSamples = points
    .filter((point) => point.timestamp.getHours() >= 21 || point.timestamp.getHours() <= 6)
    .map((point) => ({ coordinate: point, dateKey: localDateKey(point.timestamp), weight: 1 }));
  const cluster = clusterSamples(nightSamples, 0.8)
    .sort((a, b) => b.dates.size - a.dates.size || b.sampleCount - a.sampleCount)[0];
  if (!cluster || cluster.dates.size < 2) return null;
  return { coordinate: cluster, kind: 'home', label: '집 추정', confidence: 'estimated' };
}

function weekday(date: Date): boolean {
  const day = date.getDay();
  return day !== 0 && day !== 6;
}

function deriveWork(
  points: readonly TimelinePoint[],
  visits: readonly TimelineVisit[],
  home: TimelinePlace | null,
): TimelinePlace | null {
  const explicit = visits.filter((visit) => visit.semanticType === 'WORK');
  const explicitCoordinate = averageCoordinate(explicit.map((visit) => visit.coordinate));
  if (explicitCoordinate) {
    return { coordinate: explicitCoordinate, kind: 'work', label: '회사', confidence: 'explicit' };
  }

  const visitSamples = visits
    .filter((visit) => {
      const durationHours = (visit.endTime.getTime() - visit.startTime.getTime()) / 3_600_000;
      const midpoint = new Date((visit.startTime.getTime() + visit.endTime.getTime()) / 2);
      const awayFromHome = !home || coordinateDistanceKm(home.coordinate, visit.coordinate) >= 1;
      return visit.semanticType === 'UNKNOWN'
        && durationHours >= 0.75
        && durationHours <= 16
        && weekday(midpoint)
        && midpoint.getHours() >= 8
        && midpoint.getHours() <= 20
        && awayFromHome;
    })
    .map((visit) => ({
      coordinate: visit.coordinate,
      dateKey: localDateKey(visit.startTime),
      weight: Math.max(0.75, (visit.endTime.getTime() - visit.startTime.getTime()) / 3_600_000),
    }));
  const visitCluster = clusterSamples(visitSamples, 0.9)
    .filter((cluster) => cluster.dates.size >= 2 && cluster.weight >= 4)
    .sort((a, b) => b.dates.size - a.dates.size || b.weight - a.weight)[0];
  if (visitCluster) {
    return { coordinate: visitCluster, kind: 'work', label: '회사 추정', confidence: 'estimated' };
  }

  const rawSamples = points
    .filter((point) => {
      const hour = point.timestamp.getHours();
      const awayFromHome = !home || coordinateDistanceKm(home.coordinate, point) >= 1;
      return weekday(point.timestamp) && hour >= 8 && hour <= 20 && awayFromHome;
    })
    .map((point) => ({ coordinate: point, dateKey: localDateKey(point.timestamp), weight: 1 }));
  const rawCluster = clusterSamples(rawSamples, 0.75)
    .filter((cluster) => cluster.dates.size >= 3 && cluster.sampleCount >= 8)
    .sort((a, b) => b.dates.size - a.dates.size || b.sampleCount - a.sampleCount)[0];
  if (!rawCluster) return null;
  return { coordinate: rawCluster, kind: 'work', label: '회사 추정', confidence: 'estimated' };
}

function deriveCommuteDays(
  points: readonly TimelinePoint[],
  home: TimelinePlace | null,
  work: TimelinePlace | null,
): number {
  if (!home || !work) return 0;
  const days = new Map<string, { home: boolean; work: boolean }>();
  for (const point of points) {
    if (!weekday(point.timestamp)) continue;
    const key = localDateKey(point.timestamp);
    const current = days.get(key) ?? { home: false, work: false };
    if (coordinateDistanceKm(home.coordinate, point) <= 1.2) current.home = true;
    if (coordinateDistanceKm(work.coordinate, point) <= 1.2) current.work = true;
    days.set(key, current);
  }
  return [...days.values()].filter((day) => day.home && day.work).length;
}

function deriveTrips(
  points: readonly TimelinePoint[],
  activities: readonly TimelineActivity[],
  home: TimelinePlace | null,
): TimelineTrip[] {
  const candidates = new Map<string, TimelineTrip>();
  for (const activity of activities) {
    const coordinate = activity.end ?? activity.start;
    if (!coordinate || activity.distanceKm < 70) continue;
    const distanceFromHome = home ? coordinateDistanceKm(home.coordinate, coordinate) : activity.distanceKm;
    const distanceKm = Math.max(activity.distanceKm, distanceFromHome);
    const key = localDateKey(activity.startTime);
    const current = candidates.get(key);
    if (!current || current.distanceKm < distanceKm) {
      candidates.set(key, {
        startTime: activity.startTime,
        endTime: activity.endTime,
        coordinate,
        distanceKm,
        countryCode: null,
      });
    }
  }

  if (home) {
    for (const point of points) {
      const distanceKm = coordinateDistanceKm(home.coordinate, point);
      if (distanceKm < 80) continue;
      const key = localDateKey(point.timestamp);
      const current = candidates.get(key);
      if (!current || current.distanceKm < distanceKm) {
        candidates.set(key, {
          startTime: point.timestamp,
          endTime: point.timestamp,
          coordinate: point,
          distanceKm,
          countryCode: null,
        });
      }
    }
  }

  const sorted = [...candidates.values()].sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
  const trips: TimelineTrip[] = [];
  for (const candidate of sorted) {
    const previous = trips.at(-1);
    if (!previous || candidate.startTime.getTime() - previous.endTime.getTime() > 48 * 3_600_000) {
      trips.push(candidate);
      continue;
    }
    const farthest = candidate.distanceKm > previous.distanceKm ? candidate : previous;
    trips[trips.length - 1] = {
      startTime: previous.startTime,
      endTime: candidate.endTime,
      coordinate: farthest.coordinate,
      distanceKm: Math.max(previous.distanceKm, candidate.distanceKm),
      countryCode: null,
    };
  }
  return trips;
}

function routeDistanceKm(points: readonly TimelinePoint[]): number {
  let distance = 0;
  for (let index = 1; index < points.length; index += 1) {
    distance += coordinateDistanceKm(points[index - 1], points[index]);
  }
  return distance;
}

function buildAnalysis(
  points: TimelinePoint[],
  visits: TimelineVisit[],
  activities: TimelineActivity[],
  homeHints: readonly TimelineCoordinate[],
): TimelineAnalysis {
  if (points.length < 2) {
    throw new TimelineError('no-route', '선택한 기간에서는 이동 기록을 찾지 못했어요.');
  }
  const home = deriveHome(points, visits, homeHints);
  const work = deriveWork(points, visits, home);
  const recordedDays = new Set(points.map((point) => localDateKey(point.timestamp)));
  const first = points[0].timestamp.getTime();
  const last = points.at(-1)!.timestamp.getTime();
  return {
    points,
    visits,
    activities,
    home,
    work,
    commuteDays: deriveCommuteDays(points, home, work),
    trips: deriveTrips(points, activities, home),
    countries: [],
    totalDistanceKm: routeDistanceKm(points),
    periodDayCount: Math.max(1, Math.ceil((last - first) / 86_400_000) + 1),
    recordedDayCount: recordedDays.size,
  };
}

export function analyzeTimelineData(data: unknown): TimelineAnalysis {
  if (!Array.isArray(data) && !isObject(data)) {
    throw new TimelineError('unsupported', '지원하는 Google 타임라인 JSON 형식이 아닙니다.');
  }
  const semanticRecords = preferredSemanticRecords(data);
  return buildAnalysis(
    extractPoints(data, semanticRecords),
    parseVisits(semanticRecords),
    parseActivities(semanticRecords),
    collectStateHomeHints(data),
  );
}

export function analyzeTimelineJson(text: string): TimelineAnalysis {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text) as unknown;
  } catch {
    throw new TimelineError('malformed', 'JSON 파일을 읽을 수 없어요. 올바른 파일인지 확인해 주세요.');
  }
  return analyzeTimelineData(parsed);
}

/** Kept for small integrations that only need the route coordinates. */
export function parseTimelineData(data: unknown): TimelinePoint[] {
  return analyzeTimelineData(data).points;
}

export function parseTimelineJson(text: string): TimelinePoint[] {
  return analyzeTimelineJson(text).points;
}

function monthValue(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export function availableTimelineMonths(analysis: TimelineAnalysis): TimelineMonth[] {
  const values = new Set(analysis.points.map((point) => monthValue(point.timestamp)));
  return [...values].sort().map((value) => {
    const [year, month] = value.split('-').map(Number);
    return { value, label: `${year}년 ${month}월` };
  });
}

function parseMonth(value: string): { year: number; monthIndex: number } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (!Number.isInteger(year) || monthIndex < 0 || monthIndex > 11) return null;
  return { year, monthIndex };
}

export function filterTimelineAnalysis(
  analysis: TimelineAnalysis,
  startMonth: string,
  endMonth: string,
): TimelineAnalysis {
  const start = parseMonth(startMonth);
  const end = parseMonth(endMonth);
  if (!start || !end) throw new TimelineError('unsupported', '영상 기간을 다시 선택해 주세요.');
  const startTime = new Date(start.year, start.monthIndex, 1).getTime();
  const endTime = new Date(end.year, end.monthIndex + 1, 1).getTime();
  if (startTime >= endTime) throw new TimelineError('unsupported', '시작 월은 종료 월보다 빠르게 선택해 주세요.');

  const points = analysis.points.filter((point) => {
    const timestamp = point.timestamp.getTime();
    return timestamp >= startTime && timestamp < endTime;
  });
  const visits = analysis.visits.filter((visit) => (
    visit.endTime.getTime() >= startTime && visit.startTime.getTime() < endTime
  ));
  const activities = analysis.activities.filter((activity) => (
    activity.endTime.getTime() >= startTime && activity.startTime.getTime() < endTime
  ));
  const homeHints = analysis.home?.confidence === 'explicit' ? [analysis.home.coordinate] : [];
  return buildAnalysis(points, visits, activities, homeHints);
}

export function thinTimeline(points: readonly TimelinePoint[], maximum: number): TimelinePoint[] {
  if (points.length <= maximum) return [...points];
  const output: TimelinePoint[] = [];
  for (let index = 0; index < maximum; index += 1) {
    const sourceIndex = Math.round(index * (points.length - 1) / (maximum - 1));
    output.push(points[sourceIndex]);
  }
  return output;
}

export function timelinePeriod(points: readonly TimelinePoint[]): string {
  const formatter = new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const first = formatter.format(points[0].timestamp);
  const last = formatter.format(points.at(-1)!.timestamp);
  return first === last ? first : `${first} – ${last}`;
}
